/**
 * Order source provenance — pure helpers.
 *
 * Every imported marketplace order must remain traceable to its provider, its
 * specific connector/store instance, and the external order ID — never
 * collapsed to a bare `source = "ebay"`. Manual/direct sales orders (and
 * exchange orders) must stay clearly distinguishable from marketplace orders.
 *
 * These are pure, testable functions so the UI and any future consumer derive
 * provenance identically, independent of provider internals.
 */

export interface OrderProvenance {
  /** Raw `sales_orders.source` value (e.g. "ebay", "manual", "exchange"). */
  source: string;
  /** Normalised provider label (connector.provider, else the source itself). */
  provider: string | null;
  /** Connector/store display name (e.g. "Melbourne Store"). */
  storeName: string | null;
  /** Provider-scoped external order reference (e.g. "12-12345-67890"). */
  externalOrderId: string | null;
  /** True when the order came from a marketplace provider (not manual/internal). */
  isMarketplace: boolean;
}

/** Sources that represent internally-created orders, not marketplace imports. */
export const INTERNAL_SOURCES = ["manual", "api", "exchange"] as const;

export function isMarketplaceSource(source: string | null | undefined): boolean {
  const s = (source ?? "").trim().toLowerCase();
  return s.length > 0 && !(INTERNAL_SOURCES as readonly string[]).includes(s);
}

/** Friendly brand casing for known providers; falls back to title-casing. */
const PROVIDER_DISPLAY: Record<string, string> = {
  ebay: "eBay",
  bigcommerce: "BigCommerce",
};

export function capitalize(value: string): string {
  const v = value.trim();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

export function providerDisplayName(provider: string | null | undefined): string {
  const p = (provider ?? "").trim().toLowerCase();
  return PROVIDER_DISPLAY[p] ?? capitalize(p);
}

export interface ProvenanceInput {
  source: string | null | undefined;
  connectorProvider?: string | null;
  connectorName?: string | null;
  externalOrderId?: string | null;
}

export function buildProvenance(input: ProvenanceInput): OrderProvenance {
  const source = (input.source ?? "").trim();
  const connectorProvider = (input.connectorProvider ?? "").trim();
  const storeName = (input.connectorName ?? "").trim();
  const externalOrderId = (input.externalOrderId ?? "").trim();
  const marketplace = isMarketplaceSource(source);
  return {
    source,
    provider: connectorProvider || (marketplace ? source : null),
    storeName: storeName || null,
    externalOrderId: externalOrderId || null,
    isMarketplace: marketplace,
  };
}

/**
 * Full provenance line, e.g. "eBay · Melbourne Store · 12-12345-67890".
 * Omits any missing segments; a manual order yields "Manual order".
 */
export function provenanceLabel(p: OrderProvenance): string {
  if (!p.isMarketplace) {
    return p.source === "exchange" ? "Exchange" : "Manual order";
  }
  const parts: string[] = [];
  if (p.provider) parts.push(providerDisplayName(p.provider));
  if (p.storeName) parts.push(p.storeName);
  if (p.externalOrderId) parts.push(p.externalOrderId);
  return parts.join(" · ");
}

/**
 * Compact label without the external ID — used where space is tight.
 */
export function provenanceStoreLabel(p: OrderProvenance): string {
  if (!p.isMarketplace) {
    return p.source === "exchange" ? "Exchange" : "Manual";
  }
  const parts: string[] = [];
  if (p.provider) parts.push(providerDisplayName(p.provider));
  if (p.storeName) parts.push(p.storeName);
  return parts.join(" · ");
}